import { createFileRoute, redirect } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { RootLayout } from '@/components/layout/RootLayout'
import { GlassCard } from '@/components/ui/GlassCard'
import { UsageChart } from '@/components/dashboard/UsageChart'
import { usageSummaryQueryOptions } from '@/lib/queryOptions'
import { useLocale, useT } from '@/i18n/provider'
import { formatCompactNumber, formatCurrencyUsd } from '@/lib/format'

export const Route = createFileRoute('/dashboard/usage')({
  beforeLoad: async ({ context: { queryClient } }) => {
    await queryClient.ensureQueryData(usageSummaryQueryOptions(30)).catch(() => {
      throw redirect({ to: '/auth/login' })
    })
  },
  component: UsagePage,
})

function UsagePage() {
  const t = useT()
  const { locale } = useLocale()
  const { data: usage } = useQuery(usageSummaryQueryOptions(30))

  return (
    <RootLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-white">{t('usage.title')}</h1>
          <p className="text-sm text-white/40 mt-1">{t('usage.subtitle')}</p>
        </div>

        {usage ? (
          <>
            {/* Summary cards */}
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              <GlassCard size="sm">
                <p className="text-xs text-white/40 mb-1">{t('usage.totalTokens')}</p>
                <p className="text-2xl font-bold text-white">{formatCompactNumber(usage.totalTokens, locale)}</p>
              </GlassCard>
              <GlassCard size="sm">
                <p className="text-xs text-white/40 mb-1">{t('usage.totalRequests')}</p>
                <p className="text-2xl font-bold text-white">{formatCompactNumber(usage.requestCount, locale)}</p>
              </GlassCard>
              <GlassCard size="sm">
                <p className="text-xs text-white/40 mb-1">{t('usage.estCost')}</p>
                <p className="text-2xl font-bold text-white">{formatCurrencyUsd(Number(usage.totalCost), locale)}</p>
              </GlassCard>
            </div>

            {/* Timeline chart */}
            <GlassCard>
              <h2 className="text-sm font-semibold text-white/70 mb-4">{t('usage.dailyTokenUsage')}</h2>
              <UsageChart data={usage} />
            </GlassCard>

            {/* Per-provider breakdown */}
            {Object.keys(usage.byProvider).length > 0 && (
              <GlassCard>
                <h2 className="text-sm font-semibold text-white/70 mb-4">{t('usage.byProvider')}</h2>
                <div className="space-y-3">
                  {Object.entries(usage.byProvider).map(([provider, stats]) => (
                    <div key={provider} className="flex items-center justify-between py-2 border-b border-white/8 last:border-0">
                      <span className="text-sm font-medium text-white capitalize">{provider}</span>
                      <div className="text-right">
                        <p className="text-sm text-white">{formatCompactNumber(stats.tokens, locale)} {t('common.tokens')}</p>
                        <p className="text-xs text-white/40">
                          {t('usage.providerLine', {
                            requests: formatCompactNumber(stats.count, locale),
                            cost: formatCurrencyUsd(stats.cost, locale),
                          })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </GlassCard>
            )}
          </>
        ) : (
          <GlassCard className="text-center py-16">
            <p className="text-white/30">{t('usage.loading')}</p>
          </GlassCard>
        )}
      </div>
    </RootLayout>
  )
}
